import { Component, AfterViewInit, Input, OnInit } from '@angular/core';
import {
  Chart,
  ChartTypeRegistry,
  PointElement,
  registerables,
} from 'chart.js';
import {
  format,
  subDays,
  subMonths,
} from 'date-fns';
import { SalesStatisticsServiceProxy } from '../../../../../shared/service-proxies/event-service-proxy';
import { AuthService } from '../../../../services/auth/auth.service';
import { DestroyService } from '../../../../services/destroy/destroy.service';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'app-income-card',
    templateUrl: './income-card.component.html',
    styleUrls: ['./income-card.component.scss'],
    providers: [DestroyService],
    standalone: false
})
export class IncomeCardComponent implements OnInit, AfterViewInit {
  @Input() title: string = '';
  chart: any;
  selectedTimeRange: string = '1d';
  timeRanges: string[] = ['1d', '1w', '1m', '6m', '1y'];

  private timeRangeMap: { [key: string]: string } = {
    '1d': 'day',
    '1w': 'week',
    '1m': 'month',
    '6m': '6months',
    '1y': 'year'
  }
  chartType: keyof ChartTypeRegistry = 'line';

  constructor(private salesStatisticsService: SalesStatisticsServiceProxy, private authService: AuthService, private destroyService: DestroyService) {}

  ngOnInit(): void {
    this.getStatisticsAndUpdateChart();
    Chart.register(...registerables, PointElement);
  }

  ngAfterViewInit(): void {
    this.createChart([], []);
  }

  getStatisticsAndUpdateChart(): void {
    const period = this.timeRangeMap[this.selectedTimeRange] || 'day';
    this.salesStatisticsService.getStatistics(this.authService.getCompanyId(), period)
      .pipe(takeUntil(this.destroyService.onDestroy$))
      .subscribe(res => {
        console.log(res);
        this.createChart(res.income, res.expenses);
      });
  }

 createChart(incomeData: number[], expenseData: number[]): void {
    if (this.chart) {
      this.chart.destroy();
    }

    const labels = this.getLabelsForTimeRange(this.selectedTimeRange);

    const commonDatasetConfig = {
      borderCapStyle: 'round',
      tension: 0.5,
      fill: false,
      pointStyle: 'circle',
      pointRadius: 0,
      pointHoverRadius: 8,
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: '#303031',
    };

    const datasets = [
      { label: 'Доход', data: incomeData, borderColor: '#00B046', backgroundColor: '#00B046' },
      { label: 'Расход', data: expenseData, borderColor: '#D70000', backgroundColor: '#D70000' }
    ].map(dataset => ({ ...commonDatasetConfig, ...dataset }));

    this.chart = new Chart('chart', {
      type: this.chartType,
      data: { labels, datasets },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        interaction: {
          mode: 'index',
          intersect: false,
          axis: 'x',
        },
        plugins: {
          tooltip: {
            enabled: true,
            intersect: false,
            displayColors: false,
            callbacks: {
              title: () => '',
              label: (tooltipItem) => {
                const datasetLabel = tooltipItem.dataset.label;
                const value = tooltipItem.raw;
                return datasetLabel === 'Доход'
                  ? `📈 Доход: $${value}`
                  : `📉 Расход: $${value}`;
              },
            },
            bodyFont: { size: 14 },
          },
          legend: {
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 8,
              boxHeight: 8,
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { drawOnChartArea: false },
          },
          y: { grace: '1%' },
        },
      },
    });
  }

  getLabelsForTimeRange(range: string): string[] {
    const currentDate = new Date();
    switch (range) {
      case '1d':
        return Array.from({ length: 24 }, (_, i) =>
        format(subDays(currentDate, 1).setHours(i, 0, 0, 0), 'HH:mm')
      );
      case '1w':
        return Array.from({ length: 7 }, (_, i) =>
        format(subDays(currentDate, 6 - i), 'dd/MM')
      );
      case '1m':
        return Array.from({ length: 30 }, (_, i) =>
        format(subDays(currentDate, 29 - i), 'dd/MM')
      );
      case '6m':
        return Array.from({ length: 6 }, (_, i) =>
        format(subMonths(currentDate, 5 - i), 'MMM yyyy')
      );
      case '1y':
        return Array.from({ length: 12 }, (_, i) =>
        format(subMonths(currentDate, 11 - i), 'MMM yyyy')
      );
      default:
        return [format(currentDate, 'dd/MM')];
    }
  }

  onTimeRangeChange(range: string): void {
    this.selectedTimeRange = range;
    this.getStatisticsAndUpdateChart();
    
  }

  changeChartType(chartType: keyof ChartTypeRegistry) {
    this.chartType = chartType;
    this.getStatisticsAndUpdateChart();
  }
}
